import componentModel from 'decorators/componentModel';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable } from 'knockout';
import { NodeTracker } from 'managers/nodes/tracker';
import { EffectsContainer } from 'mixins/withEffects';
import WCCImage from 'models/attachments/image';
import './photoset.html';
import Photoset from './photoset.model';
import './photoset.scss';

const ComplexNumber = require('./complexNumber');
const LayoutsFactory = require('./layouts/factory');
const Item = require('./item/model');
const helpers = require('./helpers');
const states = require('./item/states');

components.registerLocal('photoset-new-item', () => require('./item/viewmodel'));

const statesAllowedToRender = [states.active, states.pending];
const instantRenderingThreshold = 10;

@injectable()
@componentModel(Photoset)
export default class PhotosetVM {
    private layout: Subscribable<any>
    private width: Subscribable<number>
    private offset: Subscribable<number>
    private rowsCount: Subscribable<number>
    private maxHeight: Subscribable<number>

    private source: Subscribable<Array<WCCImage>>
    private items: Subscribable<Array<any>>
    private rows: Subscribable<Array<any>>
    private visibleRows: Subscribable<Array<any>>
    private hiddenRows: Subscribable<Array<any>>

    private layoutsFactory: any

    id = _.uniqueId('photoset-new-');

    heightStyle: Subscribable<string>

    hiddenItemsCount: Subscribable<number>
    maxWidth: Subscribable<number | undefined>

    ready: Subscribable<boolean>
    hasHiddenItems: Subscribable<boolean>

    itemsToRender: Subscribable<Array<any>>

    cHasOneItemOnly: Subscribable<boolean>
    cNoSingleImageCaption: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) private model: Photoset,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const tracker = new NodeTracker({ node: <HTMLElement>info.element });
        this.width = tracker.width;

        this.source = model.source;
        this.rowsCount = model.rowsCount;
        const columnsCount = ko.pureComputed(() => this.getColumnsCount());        
        const noUpscale = model.noUpscale;
        const ignoreOrder = model.ignoreOrder;
        const expand = model.expand;

        this.offset = model.offset;
        const outerOffset = model.outerOffset;

        this.items = this.source.map(item => this.toPhotosetItem(item));
        this.itemsToRender = this.items.filter(item => this.shouldAllowToRender(item));
        
        this.cHasOneItemOnly = ko.pureComputed(() => this.items().length === 1);
        this.cNoSingleImageCaption = model.noSingleImageCaption;

        this.layoutsFactory = new LayoutsFactory({
            width: this.width,
            items: this.items,
            columnsCount: columnsCount,
            offset: this.offset,
            expand: expand,
            noUpscale: noUpscale,
            ignoreOrder: ignoreOrder
        });

        this.layout = ko.pureComputed(() => this.getLayout()).extend({ deferred: true });
        this.rows = this.layout.pluck('rows', []).extend({ deferred: true });

        this.visibleRows = ko.pureComputed(() => this.getVisibleRows()).extend({ deferred: true });
        this.hiddenRows = ko.pureComputed(() => this.getHiddenRows()).extend({ deferred: true });

        this.hiddenItemsCount = ko.pureComputed(() => this.getHiddenItemsCount()).extend({ deferred: true });
        this.hasHiddenItems = ko.pureComputed(() => this.hiddenItemsCount() > 0).extend({ deferred: true });

        const height = ko.pureComputed(() => this.getHeight()).extend({ deferred: true });
        this.heightStyle = height.mapSingle(helpers.toStyleValue);

        this.maxHeight = model.maxHeight;
        this.maxWidth = ko.pureComputed(() => this.getMaxWidth());

        this.ready = ko.pureComputed(() => this.source().length > 0);

        effects.register([
            expand.subscribe(_.noop), //property tracking bug fix

            model.hiddenItems.count.attachTo(this.hiddenItemsCount),

            ko.computed(this.updateVisibleRows, this).extend({ deferred: true }),
            ko.computed(this.updateItems, this).extend({ deferred: true }),
            ko.computed(this.applyLayout, this).extend({ deferred: true }),
            ko.computed(this.renderItems, this).extend({ deferred: true })
        ]);

        ko.applyBindingsToNode(info.element, {
            'visible': this.ready,

            'css': {
                'photoset-new': true,
                'photoset-new--centered': model.center
            },

            'style': {
                'padding': outerOffset.toPx()
            },

            'attr': {
                'colsCount': columnsCount
            }
        });
    }

    private shouldAllowToRender(item: any) {
        return statesAllowedToRender.includes(item.state());
    }

    private getColumnsCount() {
        var columnWidth = this.model.columnWidth(),
            result;

        if (columnWidth > 0) {
            result = Math.floor(this.width() / columnWidth);
        } else {
            result = this.model.colsCount();
        }

        return result > 0 ? result : 1;
    }

    private getLayout() {
        if (this.ready())
            return ko.ignoreDependencies(layout => this.layoutsFactory.build(layout), this, [this.model.layout()]);
    }

    private getVisibleRows() {
        return _(this.rows()).filter(row => row.isVisible());
    }

    private getHiddenRows() {
        return this.rows().filter(row => !row.isVisible());
    }

    private getHiddenItemsCount(): number {
        return this.hiddenRows().reduce((result, row) => result + row.items().length, 0);
    }

    private getHeight() {
        return _(this.visibleRows()).reduce((value, row, idx, rows) => {
            var offset = idx < rows.length - 1 ? this.offset() : 0;

            return value
                .add(row.height())
                .add(0, offset);
        }, new ComplexNumber());
    }

    private toPhotosetItem(data: WCCImage) {
        return new Item({
            index: this.source.indexOf(data),
            data: data
        });
    }

    private applyLayout() {
        ko.ignoreDependencies(function () {
            this.updateVisibleRows();
            this.updateItems();
        }, this);

        this.layout.invokeNotNull(function (layout) { layout.apply() });
    }

    private updateVisibleRows() {
        if (!this.ready()) {
            _(this.rows()).invoke('isVisible', false);
        } else if (this.maxHeight() > 0) {
            _(this.rows()).reduce((availableHeight, row, idx) => {
                availableHeight -= row.height.select((height: any) => height.rel / 100 * this.width() + height.abs);

                row.isVisible(availableHeight >= 0 || idx === 0);

                return availableHeight;
            }, this.maxHeight());
        } else if (this.rowsCount() > 0) {
            _(this.rows()).each((row, idx) => row.isVisible(idx < this.rowsCount()));
        } else {
            _(this.rows()).each(row => row.isVisible(true));
        }
    }

    private updateItems() {
        var items = this.items(),
            showCounter = this.model.hiddenItems.showCounter;

        _(items).invoke('showCounter', false);

        if (showCounter()) {
            var lastRow = _(this.visibleRows()).last();

            if (lastRow != null) {
                var lastItem = _(lastRow.items()).last();

                if (lastItem != null)
                    lastItem.showCounter(true);
            }
        }
    }

    private renderItems() {
        var items = this.items();

        if (items.length <= instantRenderingThreshold) {
            requestAnimationFrame(function () {
                _(items).each(function (item) {
                    if (item.state() === states.justCreated)
                        item.state(states.pending);
                });
            });
        } else {
            var busy = _(items).any(function (item) { return item.state() === states.pending });

            if (!busy) {
                var toRender = _(items).find(function (item) { return item.state() === states.justCreated });

                if (toRender != null) {
                    requestAnimationFrame(function () {
                        toRender.state(states.pending);
                    });
                }
            }
        }
    }

    private getMaxWidth(): number | undefined {
        var maxHeight = this.maxHeight();

        if (maxHeight > 0) {
            var row = _(this.rows()).first();

            if (row !== undefined)
                return row.height.selectNotNull((h: any) => (maxHeight - h.abs) * (100 / h.rel));
        }
    }
}